import { getUserData } from "@/auth/getUserData";
import axios from "@/libs/axios";
import { User } from "@/types/user.types";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import { UserStatus } from "@saysimple-platform/dtos";
import { useQuery } from "@tanstack/vue-query";
import { AxiosResponse } from "axios";
import { defineStore } from "pinia";

interface State {
    isLoadingUsers: boolean;
    users: User[];
}

export const useUsersStore = defineStore("users", {
    state: (): State => ({
        isLoadingUsers: false,
        users: [],
    }),
    actions: {
        fetchUsers(requestQueryBuilder?: RequestQueryBuilder, forceRefetch = false): void {
            useQuery<Pick<AxiosResponse<User[]>, "data">>(
                ["users", requestQueryBuilder?.queryObject, forceRefetch],
                () => {
                    this.isLoadingUsers = true;
                    return axios.get<User[]>(`${process.env.VUE_APP_PUBLIC_API}/users`, {
                        params: requestQueryBuilder?.queryObject,
                    });
                },
                {
                    staleTime: forceRefetch ? 0 : 300000, // 5min
                    placeholderData: { data: [] },
                    onSuccess: (response) => (this.users = response.data),
                    onSettled: () => (this.isLoadingUsers = false),
                }
            );
        },
        // @todo fix any, copy-pasted from old
        updateUser(userId: number, userData: any): void {
            // Looks a bit weird this, but is needed because of reactivity: https://v2.vuejs.org/v2/guide/reactivity.html?redirect=true#For-Arrays
            const userIndex = this.users.findIndex((user) => user.id === userId);
            const user = this.users[userIndex];

            const updatedUser = {
                ...user,
                ...userData,
            };

            this.users.splice(userIndex, 1, updatedUser);

            // Backend request. From the backend, a websocket event will be emitted.
            axios.patch(`/users/${getUserData().id}`, { status: userData.status, settings: [] }).catch(() => {});
        },
        updateUserStatus(userId: number, status: UserStatus): void {
            const user = this.users.find((user) => user.id === userId);

            if (user) {
                user.status = status;
            }
        },
    },
    getters: {
        getUserById(state): (userId: number) => User | undefined {
            return (userId) => state.users.find((user) => user.id === userId);
        },
    },
});
